import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Alert,
  Divider,
  InputAdornment,
  Chip,
  Autocomplete,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Percent as PercentIcon,
  Euro as EuroIcon,
  Warning as WarningIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const SplitBillingConfiguration = ({ 
  activity, 
  onSplitChange, 
  availablePatients = [],
  availableCompanies = [],
  initialSplits = null,
  className = "" 
}) => {
  // 🔧 INICIALIZAR CON SPLITS EXISTENTES O CREAR UNO NUEVO DEL PACIENTE
  const [splits, setSplits] = useState(() => {
    if (initialSplits && initialSplits.length > 0) {
      return initialSplits;
    }
    
    // Split por defecto del paciente
    return [{
      id: Date.now(),
      payer_id: activity.patient_id,
      payer_type: 'Account',
      percent: 100,
      max_amount: null,
      payer_snapshot: {
        name: activity.patient_name,
        display_name: activity.patient_name,
        type_description: 'Paciente',
        contact: {
          phone: activity.patient_phone || '',
          email: activity.patient_email || ''
        },
        address: activity.patient_address || {}
      }
    }];
  });
  
  const [totalPercent, setTotalPercent] = useState(100);
  const [isValid, setIsValid] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // 🔧 CALCULAR TOTAL Y VALIDAR
  useEffect(() => {
    const total = splits.reduce((sum, split) => sum + (parseFloat(split.percent) || 0), 0);
    const errors = [];
    
    // Validar porcentajes
    const percentValid = Math.abs(total - 100) <= 0.01;
    if (!percentValid) {
      errors.push(`Los porcentajes suman ${total.toFixed(2)}% (debe ser 100%)`);
    }
    
    // Validar que todos los splits tienen payer_id válido
    const emptySplits = splits.filter(split => !split.payer_id || split.payer_id.trim() === '');
    if (emptySplits.length > 0) {
      errors.push(`${emptySplits.length} pagador(es) sin seleccionar`);
    }
    
    // Validar que no hay porcentajes negativos o > 100
    const invalidPercents = splits.filter(split => split.percent < 0 || split.percent > 100);
    if (invalidPercents.length > 0) {
      errors.push(`Porcentajes inválidos detectados`);
    }
    
    // 🔧 VALIDAR QUE CADA SPLIT TIENE PAYER_SNAPSHOT
    const missingSplits = splits.filter(split => !split.payer_snapshot || !split.payer_snapshot.name);
    if (missingSplits.length > 0) {
      errors.push(`${missingSplits.length} pagador(es) sin datos completos`);
    }
    
    const valid = errors.length === 0;
    
    setTotalPercent(total);
    setValidationErrors(errors);
    setIsValid(valid);
    
    // Notificar cambios al padre
    if (onSplitChange && typeof onSplitChange === 'function') {
      const splitsCopy = splits.map(split => ({ ...split }));
      
      console.log(`✅ SplitBillingConfiguration: Notificando cambios`, {
        key: activity.key,
        splits_count: splitsCopy.length,
        isValid: valid,
        errors: errors.length
      });
      
      onSplitChange(activity.key, splitsCopy, valid);
    }
  }, [splits, activity.key]);

  // 🆕 FUNCIÓN PARA OBTENER PAYER_SNAPSHOT COMPLETO
  const buildPayerSnapshot = (payerId, payerType) => {
    let payer = null;
    
    switch (payerType) {
      case 'Account':
        payer = availablePatients.find(p => p._id === payerId);
        if (payer) {
          return {
            name: payer.fullName || `${payer.name} ${payer.surname}`,
            display_name: payer.display_name || payer.fullName,
            type_description: 'Paciente',
            contact: {
              phone: payer.phone || '',
              email: payer.email || ''
            },
            address: {
              street: payer.address || '',
              city: payer.city || '',
              state: payer.state || '',
              zipcode: payer.zipcode || ''
            }
          };
        }
        break;
        
      case 'Company':
        payer = availableCompanies.find(c => c._id === payerId);
        if (payer) {
          return {
            name: payer.name,
            display_name: payer.display_name || payer.name,
            type_description: 'Compañía',
            cif: payer.cif || '',
            contact: {
              phone: payer.phone || '',
              email: payer.email || '',
              website: payer.website || ''
            },
            address: {
              street: payer.address || '',
              city: payer.city || '',
              state: payer.state || '',
              zipcode: payer.zipcode || ''
            }
          };
        }
        break;
    }
    
    return null;
  };

  // 🆕 MANEJAR CAMBIO DE TIPO DE PAGADOR
  const handlePayerTypeChange = (index, newPayerType) => {
    console.log(`📋 Cambiando tipo de pagador del split ${index} a:`, newPayerType);
    
    const newSplits = [...splits];
    newSplits[index] = {
      ...newSplits[index],
      payer_type: newPayerType,
      payer_id: '',
      payer_snapshot: null
    };
    setSplits(newSplits);
  };

  // 🆕 MANEJAR SELECCIÓN DE PAGADOR ESPECÍFICO (AHORA CON AUTOCOMPLETE)
  const handlePayerSelection = (index, selectedPayer) => {
    console.log(`✅ Pagador seleccionado para split ${index}:`, selectedPayer);
    
    if (!selectedPayer) {
      const newSplits = [...splits];
      newSplits[index] = {
        ...newSplits[index],
        payer_id: '',
        payer_snapshot: null
      };
      setSplits(newSplits);
      return;
    }
    
    const payerId = selectedPayer._id;
    const currentPayerType = splits[index].payer_type;
    
    console.log('📦 Datos del pagador encontrado:', selectedPayer);
    
    // Construir el payer_snapshot completo
    const payerSnapshot = buildPayerSnapshot(payerId, currentPayerType);
    
    console.log('✅ Payer Snapshot construido:', payerSnapshot);
    
    // Actualizar el split con el ID y el snapshot
    const newSplits = [...splits];
    newSplits[index] = {
      ...newSplits[index],
      payer_id: payerId,
      payer_snapshot: payerSnapshot
    };
    
    setSplits(newSplits);
  };

  // 🔧 AÑADIR NUEVO SPLIT
  const addSplit = () => {
    console.log('➕ Añadiendo nuevo split');
    
    const newSplits = [...splits];
    
    // Redistribuir porcentajes
    if (totalPercent >= 100 && newSplits.length > 0) {
      const reductionPerSplit = 100 / (newSplits.length + 1);
      
      newSplits.forEach((split, index) => {
        newSplits[index] = { ...split, percent: Math.round(reductionPerSplit * 100) / 100 };
      });
    }
    
    const totalAfterReduction = newSplits.reduce((sum, s) => sum + s.percent, 0);
    const remainingPercent = Math.max(0, 100 - totalAfterReduction);
    
    const newSplit = {
      id: Date.now(),
      payer_id: '',
      payer_type: 'Company',
      percent: remainingPercent > 0 ? Math.round(remainingPercent * 100) / 100 : Math.round((100 / (newSplits.length + 1)) * 100) / 100,
      max_amount: null,
      payer_snapshot: null
    };
    
    setSplits([...newSplits, newSplit]);
  };

  // 🔧 ACTUALIZAR SPLIT
  const updateSplit = (index, field, value) => {
    console.log(`🔄 Actualizando split ${index}:`, { field, value });
    
    const newSplits = [...splits];
    newSplits[index] = { ...newSplits[index], [field]: value };
    
    setSplits(newSplits);
  };

  // 🔧 ACTUALIZAR PORCENTAJE
  const handlePercentChange = (index, value) => {
    const percent = Math.max(0, Math.min(100, parseFloat(value) || 0));
    updateSplit(index, 'percent', percent);
  };

  // 🔧 REMOVER SPLIT
  const removeSplit = (index) => {
    if (splits.length > 1) {
      const splitToRemove = splits[index];
      
      // No permitir eliminar el split del paciente si es el único de tipo User
      const userSplits = splits.filter(s => s.payer_type === 'Account');
      if (splitToRemove.payer_type === 'Account' && userSplits.length === 1) {
        alert('Debe mantener al menos un split del paciente');
        return;
      }
      
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  // 🔧 DISTRIBUIR UNIFORMEMENTE
  const distributeEvenly = () => {
    const percentPerSplit = Math.round((100 / splits.length) * 100) / 100;
    let remainder = 100 - (percentPerSplit * splits.length);
    
    const newSplits = splits.map((split, index) => ({
      ...split,
      percent: index === 0 ? percentPerSplit + remainder : percentPerSplit
    }));
    
    setSplits(newSplits);
  };

  // 🆕 OBTENER OPCIONES DE PAGADOR SEGÚN TIPO
  const getPayerOptions = (payerType) => {
    switch (payerType) {
      case 'Account':
        return availablePatients;
      case 'Company':
        return availableCompanies;
      default:
        return [];
    }
  };

  // 🔧 OBTENER NOMBRE DEL PAGADOR
  const getPayerName = (payerId, payerType) => {
    if (!payerId) return 'Seleccionar...';
    
    const options = getPayerOptions(payerType);
    const payer = options.find(p => p._id === payerId);
    
    if (payer) {
      return payer.fullName || payer.display_name || payer.name;
    }
    
    if (payerId === activity.patient_id) {
      return activity.patient_name || 'Paciente';
    }
    
    return 'Desconocido';
  };

  // 🆕 OBTENER OBJETO PAGADOR COMPLETO
  const getPayerObject = (payerId, payerType) => {
    if (!payerId) return null;
    
    const options = getPayerOptions(payerType);
    return options.find(p => p._id === payerId) || null;
  };

  // CALCULAR IMPORTE
  const calculateAmount = (percent) => {
    const total = activity.total_amount || 0;
    return (total * percent / 100).toFixed(2);
  };

  // VALIDAR SPLIT INDIVIDUAL
  const validateSplit = (split) => {
    const errors = [];
    
    if (!split.payer_id || split.payer_id.trim() === '') {
      errors.push('Debe seleccionar un pagador');
    }
    
    if (split.percent <= 0) {
      errors.push('El porcentaje debe ser mayor a 0');
    }
    
    if (split.percent > 100) {
      errors.push('El porcentaje no puede exceder 100%');
    }
    
    if (!split.payer_snapshot || !split.payer_snapshot.name) {
      errors.push('Faltan datos del pagador');
    }
    
    return errors;
  };

  // 🆕 OBTENER ICONO SEGÚN TIPO
  const getPayerIcon = (payerType) => {
    switch (payerType) {
      case 'Account':
        return <PersonIcon />;
      case 'Company':
        return <BusinessIcon />;
      default:
        return null;
    }
  };

  // 🆕 RENDERIZAR OPCIÓN EN AUTOCOMPLETE
  const renderPayerOption = (props, option, payerType) => {
    return (
      <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: payerType === 'Account' ? 'primary.main' : 'secondary.main' }}>
          {payerType === 'Account' ? <PersonIcon /> : <BusinessIcon />}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {option.fullName || option.display_name || option.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {payerType === 'Account' && option.email && `Email: ${option.email}`}
            {payerType === 'Company' && option.cif && `CIF: ${option.cif}`}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box className={className}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PercentIcon />
            Configuración de Pagadores
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {activity?.description} - Total: €{activity?.total_amount?.toFixed(2) || '0.00'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={distributeEvenly}
            disabled={splits.length === 0}
          >
            Distribuir Uniformemente
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={addSplit}
            disabled={splits.length >= 5}
          >
            Agregar Pagador
          </Button>
        </Box>
      </Box>

      {/* Lista de splits */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {splits.map((split, index) => {
          const splitErrors = validateSplit(split);
          const isPatientSplit = split.payer_id === activity.patient_id && split.payer_type === 'Account';
          const hasErrors = splitErrors.length > 0;
          const payerOptions = getPayerOptions(split.payer_type);
          const selectedPayer = getPayerObject(split.payer_id, split.payer_type);
          
          return (
            <Card 
              key={split.id || index} 
              variant="outlined"
              sx={{ 
                borderColor: hasErrors ? 'warning.main' : 'divider',
                borderWidth: hasErrors ? 2 : 1
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Tipo de pagador */}
                  <FormControl sx={{ minWidth: 200 }} size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={split.payer_type}
                      onChange={(e) => handlePayerTypeChange(index, e.target.value)}
                      disabled={isPatientSplit}
                      label="Tipo"
                      startAdornment={
                        <InputAdornment position="start">
                          {getPayerIcon(split.payer_type)}
                        </InputAdornment>
                      }
                    >
                      <MenuItem value="Account">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          Paciente
                        </Box>
                      </MenuItem>
                      <MenuItem value="Company">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" />
                          Compañía
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Selección de pagador con Autocomplete o campo fijo */}
                  {isPatientSplit ? (
                    <Box 
                      sx={{ 
                        minWidth: 250, 
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        bgcolor: 'primary.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'primary.main'
                      }}
                    >
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          {activity.patient_name || 'Paciente'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Paciente Principal
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Autocomplete
                      sx={{ minWidth: 250, flex: 1 }}
                      size="small"
                      options={payerOptions}
                      value={selectedPayer}
                      onChange={(event, newValue) => handlePayerSelection(index, newValue)}
                      getOptionLabel={(option) => option.fullName || option.display_name || option.name || ''}
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      renderOption={(props, option) => (
                        <Box 
                          component="li" 
                          {...props} 
                          key={option._id}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
                        >
                          <Avatar sx={{ width: 32, height: 32, bgcolor: split.payer_type === 'Account' ? 'primary.main' : 'secondary.main' }}>
                            {split.payer_type === 'Account' ? <PersonIcon /> : <BusinessIcon />}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {option.fullName || option.display_name || option.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {split.payer_type === 'Account' && option.email && `Email: ${option.email}`}
                              {split.payer_type === 'Company' && option.cif && `CIF: ${option.cif}`}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Buscar Pagador"
                          placeholder="Escribe para buscar..."
                          error={!split.payer_id}
                          helperText={!split.payer_id ? 'Selecciona un pagador' : ''}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      noOptionsText="No se encontraron resultados"
                      loadingText="Cargando..."
                    />
                  )}

                  {/* Porcentaje */}
                  <TextField
                    label="% Copago"
                    type="number"
                    value={split.percent}
                    onChange={(e) => handlePercentChange(index, e.target.value)}
                    size="small"
                    sx={{ width: 120 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={split.percent <= 0 || split.percent > 100}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />

                  {/* Importe calculado */}
                  <TextField
                    label="Importe"
                    value={`€${calculateAmount(split.percent)}`}
                    size="small"
                    sx={{ width: 120 }}
                    InputProps={{
                      readOnly: true,
                      startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment>,
                    }}
                    disabled
                  />

                  {/* Tope máximo */}
                  <TextField
                    label="Tope €"
                    type="number"
                    value={split.max_amount || ''}
                    onChange={(e) => updateSplit(index, 'max_amount', e.target.value ? parseFloat(e.target.value) : null)}
                    size="small"
                    sx={{ width: 120 }}
                    placeholder="Sin tope"
                    inputProps={{ min: 0, step: 0.01 }}
                  />

                  {/* Botón eliminar */}
                  <IconButton
                    color="error"
                    onClick={() => removeSplit(index)}
                    disabled={splits.length <= 1 || isPatientSplit}
                    title={isPatientSplit ? 'No se puede eliminar el split del paciente' : 'Eliminar pagador'}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                {/* Errores del split */}
                {splitErrors.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }} icon={<WarningIcon />}>
                    {splitErrors.map((error, i) => (
                      <Typography key={i} variant="caption" display="block">
                        {error}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {/* Info del payer_snapshot */}
                {split.payer_snapshot && (
                  <Chip 
                    label="✓ Datos completos cargados" 
                    size="small" 
                    color="success" 
                    variant="outlined"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Resumen */}
      <Card 
        variant="outlined"
        sx={{ 
          borderColor: isValid ? 'success.main' : 'error.main',
          borderWidth: 2
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h6" 
                color={isValid ? 'success.main' : 'error.main'}
                sx={{ fontWeight: 'bold' }}
              >
                Total porcentajes: {totalPercent.toFixed(2)}%
              </Typography>
              
              {validationErrors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {validationErrors.map((error, index) => (
                    <Alert key={index} severity="error" sx={{ mb: 0.5 }} icon={<WarningIcon />}>
                      <Typography variant="caption">{error}</Typography>
                    </Alert>
                  ))}
                </Box>
              )}
              
              <Typography 
                variant="body2" 
                color={isValid ? 'success.main' : 'error.main'}
                sx={{ mt: 1 }}
              >
                {isValid ? '✓ Configuración válida' : '✗ Configuración incompleta'}
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="textSecondary">
                Total a facturar:
              </Typography>
              <Typography variant="h5" color="success.main" sx={{ fontWeight: 'bold' }}>
                €{activity?.total_amount?.toFixed(2) || '0.00'}
              </Typography>
              
              {isValid && (
                <Box sx={{ mt: 1 }}>
                  {splits.filter(split => split.payer_id).map((split, idx) => (
                    <Typography key={split.id || idx} variant="caption" color="textSecondary" display="block">
                      {getPayerName(split.payer_id, split.payer_type)}: €{calculateAmount(split.percent)}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SplitBillingConfiguration;